import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import type { Category } from "../lib/supabase";

const FIXED_LOGO_URL =
  "https://i.ibb.co/VK4kr4t/73389a56-cadd-4ced-9410-ecc4d86db06b.jpg";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [textColor, setTextColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const fetchSettings = async () => {
      const [{ data: settings }, { data: cats }] = await Promise.all([
        supabase.from("site_settings").select("*"),
        supabase.from("categories").select("*").order("created_at"),
      ]);

      if (settings) {
        settings.forEach((row: { key: string; value: string }) => {
          if (row.key === "header_text_color" && row.value) setTextColor(row.value);
          if (row.key === "header_bg_color" && row.value) setBgColor(row.value);
        });
      }

      if (cats) setCategories(cats);
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "shadow-sm" : ""
      }`}
      style={{ backgroundColor: bgColor }}
    >
      {/* Main nav row */}
      <div className="border-b border-black/10">
        <div className="flex items-center h-16 px-4 md:px-8 gap-4">

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center text-black/70 hover:text-black transition-colors cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>

          {/* Desktop: search left */}
          <div className="hidden md:flex items-center flex-1">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="flex items-center gap-2 text-[12px] tracking-wide text-black/60 hover:text-black transition-colors cursor-pointer"
              style={{ color: textColor }}
            >
              <i className="ri-search-line text-base" />
              <span className="hidden lg:inline text-[11px] tracking-[0.1em]">Buscar</span>
            </button>
          </div>

          {/* Logo - centered */}
          <Link to="/" className="flex-shrink-0 mx-auto md:mx-0">
            <img
              src={FIXED_LOGO_URL}
              alt="Ray Melo Boutique"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop: actions right */}
          <div className="hidden md:flex items-center flex-1 justify-end gap-1">
            <div className="flex items-center gap-1">
              <a
                href="https://wa.me/5511923169450"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center hover:opacity-60 transition-opacity cursor-pointer"
                style={{ color: textColor }}
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg" />
              </a>
            </div>
          </div>

          {/* Mobile: right actions */}
          <div className="flex md:hidden items-center gap-1 ml-auto">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="w-9 h-9 flex items-center justify-center text-black/70 cursor-pointer"
              aria-label="Buscar"
            >
              <i className="ri-search-line text-lg" />
            </button>
            <a
              href="https://wa.me/5511923169450"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center text-black/70 cursor-pointer"
              aria-label="WhatsApp"
            >
              <i className="ri-whatsapp-line text-lg" />
            </a>
          </div>
        </div>

        {/* Search bar */}
        {searchOpen && (
          <div className="border-t border-black/10 px-6 py-3 bg-white">
            <div className="max-w-xl mx-auto flex items-center gap-3">
              <i className="ri-search-line text-black/40 text-base" />
              <input
                type="text"
                placeholder="O que você está procurando?"
                className="flex-1 text-sm outline-none bg-transparent py-1 placeholder:text-black/30"
                autoFocus
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="text-black/40 hover:text-black transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop: category bar (secondary) */}
      {categories.length > 6 && (
        <div className="hidden md:block border-b border-black/10 bg-white">
          <div className="flex items-center justify-center px-8 h-10 gap-1 overflow-x-auto [scrollbar-width:none]">
            <Link
              to="/catalogo"
              className="text-[10px] tracking-[0.15em] font-medium text-black/50 hover:text-black transition-colors px-3 py-2 uppercase whitespace-nowrap"
            >
              Todas as Categorias
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/categoria/${cat.slug}`}
                className="text-[10px] tracking-[0.15em] font-medium text-black/60 hover:text-black transition-colors px-3 py-2 uppercase whitespace-nowrap"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-b border-black/10 bg-white">
          <div className="px-5 py-2">
            <Link
              to="/catalogo"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-between py-3.5 border-b border-black/5 text-[12px] tracking-[0.15em] font-semibold uppercase"
            >
              Todas as Categorias
              <i className="ri-arrow-right-s-line text-black/30" />
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/categoria/${cat.slug}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between py-3.5 border-b border-black/5 text-[12px] tracking-[0.12em] uppercase text-black/70"
              >
                {cat.name}
                <i className="ri-arrow-right-s-line text-black/30" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
